// Asignación con guard: el theme editor re-inyecta los <script> de la sección al recargarla y una
// declaración top-level de class lanza "Identifier already declared" (#223). En producción no cambia nada.
window.ProductZoomHandler = window.ProductZoomHandler || class ProductZoomHandler {
    static CONFIG = {
        ZOOM_FACTOR: 3.5,
        DEBOUNCE_DELAY: 50,
        ANIMATION_DURATION: 210,
        SELECTORS: {
            GALLERY: '.product-gallery',
            PRODUCT_INFO: '.product-block-list__item--info',
            ZOOMABLE_IMAGES: '.product-gallery__carousel-item:not(.is-filtered) .product-gallery__image'
        },
        CLASSES: {
            DRIFT_PANE: 'drift-zoom-pane',
            DRIFT_OPENING: 'drift-opening',
            DRIFT_CLOSING: 'drift-closing'
        }
    };

    constructor(galleryElement) {
        this.gallery = galleryElement;
        this.zoomPanes = {};
        this.activeZoom = null; // Tracks currently active zoom
        this.debounceTimer = null; // For debouncing hide/show
        
        // Check if device is mobile
        this.isMobileDevice = window.matchMedia('(max-width: 767px), (hover: none)').matches;
        
        // Only initialize zoom if not on mobile
        if (!this.isMobileDevice) {
            this.init();
        }

        // Add resize listener to handle orientation changes
        window.addEventListener('resize', this.handleResize.bind(this));
    }

    handleResize() {
        const wasMobile = this.isMobileDevice;
        this.isMobileDevice = window.matchMedia('(max-width: 767px), (hover: none)').matches;

        // If device type changed, reinitialize or cleanup
        if (wasMobile !== this.isMobileDevice) {
            if (this.isMobileDevice) {
                this.cleanup();
            } else {
                this.init();
            }
        }
    }

    cleanup() {
        // Remove all zoom panes
        Object.values(this.zoomPanes).forEach(pane => pane.remove());
        this.zoomPanes = {};
        this.activeZoom = null;
    }

    init() {
       //console.log('Initializing zoom handler...');
        this.setupZoomContainers();
        this.attachEventListeners();
    }

    setupZoomContainers() {
        // Find all product images that should be zoomable
        const zoomableImages = this.gallery.querySelectorAll(ProductZoomHandler.CONFIG.SELECTORS.ZOOMABLE_IMAGES);
        //console.log('Found zoomable images:', zoomableImages.length);

        zoomableImages.forEach(img => {
            const mediaId = img.closest('.product-gallery__carousel-item')?.dataset.mediaId;
            //console.log('Setting up zoom for media ID:', mediaId);
            
            if (mediaId && !this.zoomPanes[mediaId]) {
                const zoomPane = this.createZoomPane();
                this.zoomPanes[mediaId] = zoomPane;
                
                // Insert zoom pane after the product gallery
                const productInfo = document.querySelector(ProductZoomHandler.CONFIG.SELECTORS.PRODUCT_INFO);
                if (productInfo) {
                    productInfo.insertBefore(zoomPane, productInfo.firstChild);
                }
            }
        });
    }

    createZoomPane() {
        const pane = document.createElement('div');
        pane.className = ProductZoomHandler.CONFIG.CLASSES.DRIFT_PANE;
        pane.style.display = 'none';
        return pane;
    }

    attachEventListeners() {
        let currentImgContainer = null;

        this.gallery.addEventListener('mouseenter', (e) => {
            const imgContainer = e.target.closest('.product-gallery__carousel-item');
            const img = imgContainer?.querySelector('.product-gallery__image');
            if (!img || !img.dataset.zoom) return;

            // Clear any pending hide operations
            if (this.debounceTimer) {
                clearTimeout(this.debounceTimer);
                this.debounceTimer = null;
            }

            currentImgContainer = imgContainer;
            //console.log('Mouse entered image:', imgContainer.dataset.mediaId);
            this.handleImageHover(img, imgContainer, e);
        }, true);

        this.gallery.addEventListener('mousemove', (e) => {
            const imgContainer = e.target.closest('.product-gallery__carousel-item');
            const img = imgContainer?.querySelector('.product-gallery__image');
            if (!img || !img.dataset.zoom) return;
            
            // Update current container reference
            currentImgContainer = imgContainer;
            this.handleImageMove(img, imgContainer, e);
        }, true);

        this.gallery.addEventListener('mouseleave', (e) => {
            if (!currentImgContainer) return;

            // Debounce the hide operation
            this.debounceTimer = setTimeout(() => {
                // Only log if currentImgContainer still exists and has mediaId
                if (currentImgContainer?.dataset?.mediaId) {
                    //console.log('Mouse left image:', currentImgContainer.dataset.mediaId);
                }
                this.hideZoomPane(currentImgContainer);
                currentImgContainer = null;
            }, ProductZoomHandler.CONFIG.DEBOUNCE_DELAY); // Small delay to prevent flickering
        }, true);
    }

    handleImageHover(img, container, event) {
        const mediaId = container.dataset.mediaId;
        const zoomPane = this.zoomPanes[mediaId];
        if (!zoomPane) return;

        // If there's an active zoom and it's different from the current one, hide it
        if (this.activeZoom && this.activeZoom !== zoomPane) {
            this.activeZoom.style.display = 'none';
            this.activeZoom.classList.remove(ProductZoomHandler.CONFIG.CLASSES.DRIFT_OPENING, ProductZoomHandler.CONFIG.CLASSES.DRIFT_CLOSING);
        }

        // Set current active zoom
        this.activeZoom = zoomPane;

        // Load high-res image if not already loaded
        if (!zoomPane.style.backgroundImage) {
            const zoomImg = new Image();
            zoomImg.src = img.dataset.zoom;
            zoomPane.style.backgroundImage = `url(${img.dataset.zoom})`;
        }

        // Show zoom pane if not already visible
        if (zoomPane.style.display === 'none') {
            zoomPane.style.display = 'block';
            zoomPane.classList.add(ProductZoomHandler.CONFIG.CLASSES.DRIFT_OPENING);
            setTimeout(() => zoomPane.classList.remove(ProductZoomHandler.CONFIG.CLASSES.DRIFT_OPENING), ProductZoomHandler.CONFIG.ANIMATION_DURATION);
        }

        this.updateZoomPosition(img, event, zoomPane);
    }

    handleImageMove(img, container, event) {
        const mediaId = container.dataset.mediaId;
        const zoomPane = this.zoomPanes[mediaId];
        if (!zoomPane || zoomPane.style.display === 'none') return;

        this.updateZoomPosition(img, event, zoomPane);
    }

    updateZoomPosition(img, event, zoomPane) {
        const rect = img.getBoundingClientRect();
        
        // Calculate mouse position relative to image
        const mouseX = event.clientX - rect.left;
        const mouseY = event.clientY - rect.top;
        
        // Add boundary check
        if (mouseX < 0 || mouseX > rect.width || mouseY < 0 || mouseY > rect.height) {
            return;
        }
        
        // Calculate percentage positions
        const xPercent = (mouseX / rect.width) * 100;
        const yPercent = (mouseY / rect.height) * 100;

        // Calculate background position with increased zoom factor
        const bgPosX = (xPercent * ProductZoomHandler.CONFIG.ZOOM_FACTOR) - (xPercent * (ProductZoomHandler.CONFIG.ZOOM_FACTOR - 1));
        const bgPosY = (yPercent * ProductZoomHandler.CONFIG.ZOOM_FACTOR) - (yPercent * (ProductZoomHandler.CONFIG.ZOOM_FACTOR - 1));
        
        zoomPane.style.backgroundPosition = `${bgPosX}% ${bgPosY}%`;
        zoomPane.style.backgroundSize = `${ProductZoomHandler.CONFIG.ZOOM_FACTOR * 100}%`;
    }

    hideZoomPane(container) {
        // Add safety check for container and its dataset
        if (!container?.dataset?.mediaId) return;
        
        const mediaId = container.dataset.mediaId;
        const zoomPane = this.zoomPanes[mediaId];
        if (!zoomPane || zoomPane.style.display === 'none') return;

        zoomPane.classList.add(ProductZoomHandler.CONFIG.CLASSES.DRIFT_CLOSING);
        setTimeout(() => {
            zoomPane.classList.remove(ProductZoomHandler.CONFIG.CLASSES.DRIFT_CLOSING);
            zoomPane.style.display = 'none';
            if (this.activeZoom === zoomPane) {
                this.activeZoom = null;
            }
        }, ProductZoomHandler.CONFIG.ANIMATION_DURATION);
    }

    reinitialize() {
        //console.log('Reinitializing zoom handler...');
        // Clear existing zoom panes
        Object.values(this.zoomPanes).forEach(pane => pane.remove());
        this.zoomPanes = {};
        this.activeZoom = null;
        
        // Reinitialize
        this.setupZoomContainers();
    }
};

// Init: arranca al cargar el DOM; si el script se reinyecta (theme editor) cuando el DOM
// ya está listo, inicializa de inmediato. Idempotente vía window.productZoom.
(function () {
    function initProductZoom() {
        if (window.productZoom) return;
        const galleryElement = document.querySelector(window.ProductZoomHandler.CONFIG.SELECTORS.GALLERY);
        if (galleryElement) {
            window.productZoom = new window.ProductZoomHandler(galleryElement);
        }
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initProductZoom);
    } else {
        initProductZoom();
    }
})(); 